import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { PrinterSettingsModal } from '../../components/Printer/PrinterSettingsModal';
import { ActionsCard } from '../../components/Printer/ActionsCard';
import { ConfigurationStatusCard } from '../../components/Printer/ConfigurationStatusCard';
import { PrintTestCard } from '../../components/Printer/PrintTestCard';
import { XmlCommandCard } from '../../components/Printer/XmlCommandCard';
import { NovCommandCard } from '../../components/Printer/NovCommandCard';
import { GetProtectedMemoryCard } from '../../components/Printer/GetProtectedMemoryCard';
import { NovitusNonFiscalPayloadDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import { usePrinterService } from '../../../common/hooks/usePrinterService';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';

export const PrinterAdminView: React.FC = () => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEReceiptConfigLoading, setIsEReceiptConfigLoading] = useState(false);
  const [isSyncLoading, setIsSyncLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{
    synced: number;
    total: number;
  } | null>(null);
  const syncAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      syncAbortControllerRef.current?.abort();
    };
  }, []);

  const printerService = usePrinterService();
  const spd = useSpdCore();
  const auth = useAuth();
  const isSuperAdmin = auth.check([Role.SuperAdmin]);
  const isConfigured = printerService.isConfigured();
  const printerUrl = printerService.getConfig()?.apiUrl || 'N/A';
  const printerUniqueId = printerService.getStoredUniqueId();

  const handlePrintTestText = async () => {
    setIsLoading(true);
    try {
      const testData: NovitusNonFiscalPayloadDto = {
        printout: {
          lines: [{ textline: { text: 'TEKST TESTOWY', masked: false } }],
        },
      };

      const result = await printerService.printNonFiscalText(testData);
      toast.success(`Druk ukończony. ID dokumentu: ${result.jpkid}`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Błąd drukowania';
      console.error('Error during printing:', err);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncDocuments = async () => {
    if (!printerUniqueId) {
      toast.error('Brak identyfikatora drukarki');
      return;
    }
    setIsSyncLoading(true);
    setSyncProgress(null);
    const controller = new AbortController();
    syncAbortControllerRef.current = controller;
    try {
      await spd.printerJpkDocuments.sync(printerUniqueId, {
        onProgress: (synced, total) => setSyncProgress({ synced, total }),
        signal: controller.signal,
      });
      if (controller.signal.aborted) {
        toast.info('Synchronizacja anulowana');
      } else {
        toast.success('Synchronizacja dokumentów zakończona');
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Błąd synchronizacji dokumentów';
      console.error('Error during JPK sync:', err);
      toast.error(errorMsg);
    } finally {
      setIsSyncLoading(false);
      setSyncProgress(null);
    }
  };

  const handleSendEReceiptConfig = async () => {
    setIsEReceiptConfigLoading(true);
    try {
      const response = await spd.fiscalPrinter.generateEReceiptConfig();
      await printerService.sendXmlCommand(response.xml);
      toast.success('Konfiguracja e-paragonów wysłana');
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Błąd wysyłania konfiguracji';
      console.error('Error sending e-receipt config:', err);
      toast.error(errorMsg);
    } finally {
      setIsEReceiptConfigLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Zarządzanie drukarką fiskalną</h1>
        </Col>
      </Row>
      <ActionsCard
        isConfigured={isConfigured}
        isEReceiptConfigLoading={isEReceiptConfigLoading}
        onSendEReceiptConfig={handleSendEReceiptConfig}
        isSyncLoading={isSyncLoading}
        syncProgress={syncProgress}
        onSyncDocuments={handleSyncDocuments}
      />
      <ConfigurationStatusCard
        isConfigured={isConfigured}
        printerUrl={printerUrl}
        uniqueId={printerUniqueId}
        onShowSettings={() => setShowSettingsModal(true)}
      />
      {isConfigured && (
        <>
          <PrintTestCard
            isLoading={isLoading}
            onPrintTestText={handlePrintTestText}
          />
          {isSuperAdmin && (
            <>
              <XmlCommandCard />
              <NovCommandCard />
              <GetProtectedMemoryCard />
            </>
          )}
        </>
      )}

      <PrinterSettingsModal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        onConfigSaved={() => {
          toast.success('Ustawienia drukarki zostały zapisane');
        }}
      />
    </Container>
  );
};
